import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/lib/supabase';
import { Badge } from '@/components/ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Clock, AlertTriangle } from 'lucide-react';
import { cn } from '@/lib/utils';

// Mock data for simulation
const MOCK_DATA = [
  { id: '1', userName: 'John Smith', avatarUrl: null, userId: '1', requirementName: 'First Aid Certificate', daysUntil: 3 },
  { id: '2', userName: 'Sarah Johnson', avatarUrl: null, userId: '2', requirementName: 'CSCS Card', daysUntil: 7 },
  { id: '3', userName: 'Mike Williams', avatarUrl: null, userId: '3', requirementName: 'Manual Handling', daysUntil: 12 },
  { id: '4', userName: 'Emma Brown', avatarUrl: null, userId: '4', requirementName: 'Fire Safety', daysUntil: 18 },
  { id: '5', userName: 'David Lee', avatarUrl: null, userId: '5', requirementName: 'Working at Height', daysUntil: 25 },
  { id: '6', userName: 'Lisa Chen', avatarUrl: null, userId: '6', requirementName: 'Asbestos Awareness', daysUntil: 28 },
];

export function ExpiringSoonTile({ filter, useMockData }) {
  const navigate = useNavigate();
  const { company } = useAuth();
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dimensions, setDimensions] = useState({ width: 0, height: 0 });
  const containerRef = useRef(null);

  // Observe container size changes
  useEffect(() => {
    if (!containerRef.current) return;

    const observer = new ResizeObserver((entries) => {
      const entry = entries[0];
      if (entry) {
        setDimensions({
          width: entry.contentRect.width,
          height: entry.contentRect.height,
        });
      }
    });

    observer.observe(containerRef.current);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (useMockData) {
      setData(MOCK_DATA);
      setLoading(false);
      return;
    }

    if (!company?.id) {
      setLoading(false);
      return;
    }

    const loadData = async () => {
      setLoading(true);
      try {
        // Get expiring qualifications (within 30 days)
        const today = new Date();
        const thirtyDaysFromNow = new Date();
        thirtyDaysFromNow.setDate(thirtyDaysFromNow.getDate() + 30);

        // First, get all user IDs for this company
        let userIdsToQuery = [];

        if (filter?.spaceId) {
          // Get team members for this specific space
          const { data: teamMembers } = await supabase
            .from('team_members')
            .select('user_id')
            .eq('project_id', filter.spaceId);

          userIdsToQuery = (teamMembers || []).map(tm => tm.user_id);
        } else {
          // Get all users in the company
          const { data: companyUsers } = await supabase
            .from('user_profiles')
            .select('id')
            .eq('company_id', company.id);

          userIdsToQuery = (companyUsers || []).map(u => u.id);
        }

        if (userIdsToQuery.length === 0) {
          setData([]);
          setLoading(false);
          return;
        }

        // Now query qualifications for these users
        const { data: qualifications, error } = await supabase
          .from('user_qualifications')
          .select(`
            id,
            expiry_date,
            user_id,
            requirement_id,
            requirement:compliance_requirements(id, name)
          `)
          .in('user_id', userIdsToQuery)
          .not('expiry_date', 'is', null)
          .gte('expiry_date', today.toISOString().split('T')[0])
          .lte('expiry_date', thirtyDaysFromNow.toISOString().split('T')[0])
          .order('expiry_date', { ascending: true })
          .limit(10);

        if (error) throw error;

        // Get unique user IDs from qualifications
        const qualUserIds = [...new Set((qualifications || []).map(q => q.user_id))];

        // Fetch user profiles for these users (if any)
        let userProfiles = [];
        if (qualUserIds.length > 0) {
          const { data } = await supabase
            .from('user_profiles')
            .select('id, first_name, last_name, avatar_url')
            .in('id', qualUserIds);
          userProfiles = data || [];
        }

        // Create a lookup map
        const userMap = userProfiles.reduce((acc, u) => {
          acc[u.id] = u;
          return acc;
        }, {});

        const items = (qualifications || []).map(q => {
          const user = userMap[q.user_id] || {};
          return {
            id: q.id,
            userName: `${user.first_name || ''} ${user.last_name || ''}`.trim() || 'Unknown',
            avatarUrl: user.avatar_url,
            userId: q.user_id,
            requirementName: q.requirement?.name || 'Unknown',
            expiryDate: new Date(q.expiry_date),
            daysUntil: Math.ceil((new Date(q.expiry_date) - new Date()) / (1000 * 60 * 60 * 24)),
          };
        });

        setData(items);
      } catch (err) {
        console.error('Failed to load expiring data:', err);
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [company?.id, filter?.spaceId, useMockData]);

  // Determine layout based on dimensions
  const { width, height } = dimensions;
  const isXS = width < 150 || height < 100;
  const isSmall = width < 200 || height < 150;
  const isTall = height > width * 1.2;
  const isWide = width > height * 1.8;
  const isLarge = width >= 350 && height >= 300;

  // Calculate how many items to show based on available height
  const getVisibleCount = () => {
    if (isXS) return 1;
    if (isSmall) return 2;
    const itemHeight = 44; // approximate height per item
    const headerHeight = isLarge ? 0 : 0;
    const availableHeight = height - headerHeight - 20;
    return Math.max(1, Math.floor(availableHeight / itemHeight));
  };

  const getDaysColor = (days) => {
    if (days <= 7) return 'destructive';
    if (days <= 14) return 'warning';
    return 'secondary';
  };

  const getDaysTextColor = (days) => {
    if (days <= 7) return 'text-red-600';
    if (days <= 14) return 'text-amber-600';
    return 'text-gray-600';
  };

  const getDaysBgColor = (days) => {
    if (days <= 7) return 'bg-red-50';
    if (days <= 14) return 'bg-amber-50';
    return 'bg-gray-50';
  };

  if (loading) {
    return (
      <div ref={containerRef} className="flex items-center justify-center h-full">
        <div className="animate-pulse space-y-2 w-full p-2">
          {[...Array(3)].map((_, i) => (
            <div key={i} className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-full bg-gray-200 shrink-0" />
              <div className="flex-1">
                <div className="h-3 bg-gray-200 rounded w-3/4 mb-1" />
                <div className="h-2 bg-gray-200 rounded w-1/2" />
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (data.length === 0) {
    return (
      <div ref={containerRef} className="flex flex-col items-center justify-center h-full text-gray-400">
        <Clock className="h-8 w-8 mb-2" />
        <span className="text-sm">Nothing expiring soon</span>
      </div>
    );
  }

  const visibleItems = data.slice(0, getVisibleCount());
  const remainingCount = data.length - visibleItems.length;

  // XS Layout: Just count and icon
  if (isXS) {
    const urgentCount = data.filter(d => d.daysUntil <= 7).length;
    return (
      <div ref={containerRef} className="flex items-center justify-center h-full overflow-visible">
        <div className="text-center">
          <div className={cn('text-3xl font-bold leading-none', urgentCount > 0 ? 'text-amber-600' : 'text-gray-600')}>
            {data.length}
          </div>
          <p className="text-xs text-gray-500 mt-1">expiring</p>
        </div>
      </div>
    );
  }

  // Small Layout: Count + first item preview
  if (isSmall) {
    const urgentCount = data.filter(d => d.daysUntil <= 7).length;
    return (
      <div ref={containerRef} className="flex flex-col h-full p-2">
        <div className="flex items-center gap-2 mb-2">
          <div className={cn('text-2xl font-bold', urgentCount > 0 ? 'text-amber-600' : 'text-gray-600')}>
            {data.length}
          </div>
          <div className="text-xs text-gray-500">
            <div>expiring</div>
            <div>within 30d</div>
          </div>
        </div>
        {visibleItems.slice(0, 2).map((item) => (
          <div key={item.id} className="flex items-center gap-2 py-1">
            <div className={cn('w-1.5 h-1.5 rounded-full shrink-0',
              item.daysUntil <= 7 ? 'bg-red-500' : item.daysUntil <= 14 ? 'bg-amber-500' : 'bg-gray-400'
            )} />
            <span className="text-xs truncate flex-1">{item.requirementName}</span>
            <span className={cn('text-xs font-medium', getDaysTextColor(item.daysUntil))}>
              {item.daysUntil}d
            </span>
          </div>
        ))}
      </div>
    );
  }

  // Wide Layout: Horizontal list
  if (isWide && !isLarge) {
    return (
      <div ref={containerRef} className="flex items-center h-full gap-3 p-2 overflow-hidden">
        {/* Summary */}
        <div className="shrink-0 text-center px-2">
          <div className="text-3xl font-bold text-amber-600">{data.length}</div>
          <p className="text-xs text-gray-500">expiring</p>
        </div>

        {/* Items */}
        <div className="flex-1 flex gap-2 overflow-hidden">
          {visibleItems.slice(0, 4).map((item) => (
            <div
              key={item.id}
              onClick={() => navigate(`/profile/${item.userId}`)}
              className={cn(
                "flex items-center gap-2 px-2 py-1.5 rounded-lg cursor-pointer shrink-0",
                getDaysBgColor(item.daysUntil)
              )}
            >
              <Avatar className="h-6 w-6">
                <AvatarImage src={item.avatarUrl} />
                <AvatarFallback className="text-[10px]">
                  {item.userName.split(' ').map(n => n[0]).join('')}
                </AvatarFallback>
              </Avatar>
              <div className="min-w-0">
                <p className="text-xs font-medium truncate max-w-[100px]">{item.requirementName}</p>
                <p className={cn('text-[10px] font-semibold', getDaysTextColor(item.daysUntil))}>
                  {item.daysUntil}d
                </p>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  // Large Layout: Full list with details
  if (isLarge) {
    return (
      <div ref={containerRef} className="flex flex-col h-full p-2">
        {/* Header summary */}
        <div className="flex items-center justify-between mb-3 pb-2 border-b">
          <div className="flex items-center gap-2">
            <Clock className="h-5 w-5 text-amber-500" />
            <span className="font-medium">Expiring Soon</span>
          </div>
          <div className="flex items-center gap-2">
            {data.filter(d => d.daysUntil <= 7).length > 0 && (
              <Badge variant="destructive" className="text-xs">
                {data.filter(d => d.daysUntil <= 7).length} urgent
              </Badge>
            )}
            <Badge variant="secondary">{data.length} total</Badge>
          </div>
        </div>

        {/* List */}
        <div className="flex-1 space-y-1.5 overflow-y-auto">
          {visibleItems.map((item) => (
            <div
              key={item.id}
              onClick={() => navigate(`/profile/${item.userId}`)}
              className={cn(
                "flex items-center gap-3 p-2 rounded-lg cursor-pointer transition-colors",
                getDaysBgColor(item.daysUntil),
                "hover:opacity-80"
              )}
            >
              <Avatar className="h-9 w-9 shrink-0">
                <AvatarImage src={item.avatarUrl} />
                <AvatarFallback className="text-xs">
                  {item.userName.split(' ').map(n => n[0]).join('')}
                </AvatarFallback>
              </Avatar>

              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium truncate">{item.requirementName}</p>
                <p className="text-xs text-gray-500 truncate">{item.userName}</p>
              </div>

              <div className="text-right shrink-0">
                <div className={cn('text-sm font-bold', getDaysTextColor(item.daysUntil))}>
                  {item.daysUntil <= 0 ? 'Today' : `${item.daysUntil}d`}
                </div>
                <div className="text-[10px] text-gray-400">remaining</div>
              </div>
            </div>
          ))}
        </div>

        {/* Footer */}
        {remainingCount > 0 && (
          <button
            onClick={() => navigate('/qualifications')}
            className="mt-2 pt-2 border-t text-center text-sm text-blue-600 hover:text-blue-700"
          >
            +{remainingCount} more
          </button>
        )}
      </div>
    );
  }

  // Default/Medium Layout: Vertical list
  return (
    <div ref={containerRef} className="flex flex-col h-full p-2">
      <div className="flex-1 space-y-1">
        {visibleItems.map((item) => (
          <div
            key={item.id}
            onClick={() => navigate(`/profile/${item.userId}`)}
            className="flex items-center gap-2 p-1.5 rounded-lg hover:bg-gray-50 cursor-pointer transition-colors"
          >
            <Avatar className="h-7 w-7 shrink-0">
              <AvatarImage src={item.avatarUrl} />
              <AvatarFallback className="text-[10px]">
                {item.userName.split(' ').map(n => n[0]).join('')}
              </AvatarFallback>
            </Avatar>

            <div className="flex-1 min-w-0">
              <p className="text-xs font-medium truncate">{item.requirementName}</p>
              <p className="text-[10px] text-gray-500 truncate">{item.userName}</p>
            </div>

            <Badge variant={getDaysColor(item.daysUntil)} className="shrink-0 text-[10px] px-1.5 py-0">
              {item.daysUntil <= 0 ? 'Today' : `${item.daysUntil}d`}
            </Badge>
          </div>
        ))}
      </div>

      {remainingCount > 0 && (
        <button
          onClick={() => navigate('/qualifications')}
          className="text-xs text-blue-600 hover:text-blue-700 pt-1"
        >
          +{remainingCount} more
        </button>
      )}
    </div>
  );
}
